import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Table, { ColumnType } from "components/Table";
import useTitle from "hooks/useTitle";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { ResType } from "types";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetFilters } from "redux/reducers/taskboardSlice";
import { Visibility } from "@mui/icons-material";
import {
  exportIncomeTaxEproceedings,
  getFyaSections,
  getIncomeTaxEproceedings,
} from "api/services/automation/income-tax";
import { dropDownType, getAutFinancialYears } from "pages/atom-pro/constants";
import SearchContainer from "components/SearchContainer";
import Loader from "components/Loader";
import { handleError } from "utils/handleError";
import { getCurrentFormattedDateTime } from "utils";
import { snack } from "components/toast";
import { Permissions } from "data/permissons";
import { usePermissions } from "context/PermissionsProvider";
import AccessDenied from "views/atom-pro/Access/accessDenide";
import { StyledSpan } from "./styles";
import FilterEngine from "pages/atom-pro/Filters";
import { buildChips } from "pages/atom-pro/Filters/buildChips";
import excelIcon from "./../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import clearFilters from "./../../assets/images/clear-filter.svg";
import { hasActiveFilters } from "pages/atom-pro/Filters/components";
import moment from "moment";
import useClientCategories from "hooks/useClientCategories";


const EproceedingFya = () => {
  useTitle("ATOM Pro | e-Proceedings");
  const [search, setSearch] = useState("");
  const { permissions } = usePermissions();
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const dispatch = useDispatch();
  const [sections, setSectionData] = useState<any>(null);
  const location = useLocation();
  const state: any = location.state;
  const {
    categories,
  } = useClientCategories({});
  const clientCategoryOptions =
    useMemo(() => {

      return categories.reduce(
        (acc: any, item: any) => {

          acc[String(item.id)] =
            item.name;

          return acc;

        },
        {},
      );

    }, [categories]);
  const assessmentYears =
    useMemo(
      () => getAutFinancialYears(),
      [],
    );
  const [filters, setFilters] = useState<any>({
    section: null,
    assessmentYear: null,
    clientCategory: null,
    type: null
  });

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  const { data, isLoading }: ResType = useQuery(
    [
      "e-proceedings",
      {
        search: search,
        limit: pageCount,
        offset: page * pageCount,
        ...filters
      },
    ],
    getIncomeTaxEproceedings,
    {
      onSuccess: (res) => {
        setTotalCount(res?.data?.count);
      },
      enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)
    }
  );

  const { data: sectionData, isLoading: sectionIsloading }: ResType = useQuery(
    ["e-proceedings-sections"],
    getFyaSections,
    {
      onSuccess: (res) => {
        setSectionData(res?.data);
      },
      enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)
    }
  );


  const handleSearch = (e) => {
    setSearch(e);
    setPage(0);
  };


  const { mutate: mutate1, isLoading: isExporting } = useMutation(exportIncomeTaxEproceedings, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      const formattedDateTime = getCurrentFormattedDateTime();

      let link = document.createElement("a");
      link.href = file;
      link.download = `ATOM Pro - Income Tax (PAN) e-Proceedings (For Your Action) ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Income Tax (PAN) e-proceedings exported sucessfully");
    },
    onError: (err: any) => {
      snack.error(err);
      snack.error(handleError(err));
    },
  });
  const handleExport = () => {
    if (isExporting) return;
    mutate1({
      ...state,
      search: search,
      limit: pageCount,
      offset: page * pageCount,
      ...filters
    });
  };

  const filterConfig = useMemo(() => ({
    assessmentYear: {
      label: "Assessment Year",
      type: "year",
      options: assessmentYears,
      getValue: (year) => year.split("-")[0],
    },
    section: {
      label: "Section",
      type: dropDownType.array,
      options: sections,
    },
    type: {
      label: "Type",
      type: dropDownType.array,
      options: ["Self", "Other"],
    },
    clientCategory: {
      label: "Category",
      type: dropDownType.object,
      options: clientCategoryOptions,
    },
  }), [
    assessmentYears,
    clientCategoryOptions,
    sections,
  ]);

  const chips: any = buildChips(filters, filterConfig);

  const isFilterActive = hasActiveFilters(filters);

  if (!(permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS))) {
    return <AccessDenied />
  }
  if (isLoading || sectionIsloading) return <Loader />

  return (
    <>
      <Box m={1}>
        <Box mt={1}>
          <Box mb={1} display="flex" alignContent="center" justifyContent='space-between'>
            <SearchContainer
              debounced
              value={search}
              placeHolder="Search by Client | PAN | Proceeding Name"
              onChange={handleSearch}
            />
            <Box display='flex'>

              <FilterEngine filters={filters} config={filterConfig} setFilters={setFilters} setPage={setPage} />
              {isFilterActive && (
                <Tooltip title={'Reset Filters'}>
                  <img
                    src={clearFilters}
                    alt="Export"
                    style={{ cursor: 'pointer', width: 38, height: 38, marginRight: "5px", marginLeft: "5px" }}
                    onClick={() => {
                      setFilters({
                        section: null,
                        assessmentYear: null,
                        clientCategory: null,
                        type: null
                      });
                      setSearch("");
                    }}
                  />
                </Tooltip>)}

              <Button disabled={isExporting}>
                <Tooltip title={isExporting ? "Exporting..." : "Data Export to Excel"}>
                  <img
                    src={excelIcon}
                    alt="Export"
                    style={{ cursor: isExporting ? "not-allowed" : "pointer", width: 30, height: 30, opacity: isExporting ? 0.5 : 1 }}
                    onClick={handleExport}
                    aria-disabled={isExporting}
                  />
                </Tooltip>
              </Button>
            </Box>
          </Box>
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 1, ml: 2, mt: 1, flexWrap: "wrap" }}>
              {chips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  onDelete={() => {
                    const updated = {
                      ...filters,
                      [chip.key]:
                        chip.key === "dates"
                          ? {
                            fromDate: null,
                            toDate: null,
                          }
                          : null,
                    };
                    setFilters(updated);
                  }}
                  variant="outlined"
                  color="primary"
                />
              ))}
            </Stack>


          </Box>
          <Table
            sx={{ pb: 0, mb: 0, height: "475px" }}
            pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
            data={data?.data.result || []}
            columns={columns}
            loading={isLoading}
          // onRowClick={handleRowClick}
          />
        </Box>
      </Box>
    </>
  );
};

const generateAssessmentYear = (assessmentYear) => {
  if (!assessmentYear || assessmentYear === "0" || assessmentYear === "") {
    return "NA";
  } else if (!isNaN(assessmentYear) && assessmentYear.length === 4) {
    const year = parseInt(assessmentYear);
    const nextYear = (year + 1).toString().slice(-2);
    return `AY ${year}-${nextYear}`;
  } else {
    return assessmentYear;
  }
};


const columns: Array<ColumnType> = [
  {
    key: "assesmentYear",
    title: "Assessment Year",
    render: (v) => (
      <Typography
      >
        {generateAssessmentYear(v?.assesmentYear)}
      </Typography>
    ),
  },
  { key: "type", title: "Type" },
  // {
  //   key: "",
  //   title: (
  //     <Box display="flex" alignItems="center">
  //       Category
  //     </Box>
  //   ),
  //   render: (v) => {
  //     const categoryLabel =
  //       clientCategories.find((category) => category.value === v?.client?.category)?.label || "";

  //     return (
  //       <Typography
  //       >
  //         {categoryLabel}
  //       </Typography>
  //     );
  //   },
  // },

  {
    key: "",
    title: (
      <Box display="flex" alignItems="center">
        Client Name (PAN)
      </Box>
    ),
    render: (v) => (
      <Typography
      >
        {v?.client?.displayName} (
        <span
        >
          {v?.client?.autClientCredentials[0]?.panNumber}
        </span>
        )
      </Typography>
    ),
  },

  {
    key: "noticeName",
    title: (
      <Box display="flex" alignItems="center">
        Section
      </Box>
    ),
    render: (v) => (
      <Typography
      >
        {v?.noticeName ? (
          <span
          >
            {v?.noticeName}
          </span>
        ) : (
          "--"
        )}
      </Typography>
    ),
  },
  {
    key: "sectionCodeText",
    title: "Proceeding Name",
    render: (v) => (
      <Typography
      >
        {v.proceedingName}
      </Typography>
    ),
  },
  {
    key: "",
    title: (
      <Box display="flex" alignItems="center">
        Assessee Name (PAN)
      </Box>
    ),
    render: (v) => {
      const displayName = (v?.type === "Self" && v?.notices[0]?.nameOfAssesse) ? v?.notices[0]?.nameOfAssesse : "--";
      return <Typography>{displayName} {v?.type === "Other" && (<span>{'(' + v?.pan + ')'}</span>)}</Typography>;
    },
  },
  {
    key: "proceedingOpenDate",
    title: "Proceeding Opened on",
    render: (v) => (
      <Typography
      >
        {v?.proceedingOpenDate ? moment(Number(v?.proceedingOpenDate)).format("DD-MM-YYYY HH:mm") : "-"}
      </Typography>
    ),
  },
  {
    key: "id",
    title: "Created Type",
    render: (v) =>
      <Box>
        {v?.createdType === "MANUAL" ? (
          <StyledSpan bgColor="#f2e6ff">Manual</StyledSpan>
        ) : (
          <StyledSpan bgColor="#b3ecff">Portal</StyledSpan>
        )}
      </Box>
  },
  {
    key: "",
    title: "Action",
    render: (rowData: any) => <Actions data={rowData} />,
  },
];

function Actions(props: any) {
  const navigate = useNavigate();

  const handleViewMoreClick = () => {
    navigate(`/atom-pro/income-tax/proceeding-action/${props.data?.id}`);
  };

  return (
    <IconButton onClick={handleViewMoreClick} size="small">
      <Visibility />
    </IconButton>
  );
}

export default EproceedingFya;
